import { DatapointStatus, lazyLogSigsDatapoint, PerformanceDatapoint } from 'owa-analytics';
import { AttachmentSelectionSource } from 'owa-attachment-data';
import {
    getSaveToCloudEstimatedCompleteTimeInMS,
    saveToCloudPostProcess,
    saveToCloudPreProcess,
    setActionCompletePercent,
    startFakeProgressIndicator,
    stopFakeProgressIndicator,
} from 'owa-attachment-full-data';
import { lazyGetAttachment } from 'owa-attachment-model-store';
import setAttachmentOriginalUrl from 'owa-attachment-model-store/lib/actions/setAttachmentOriginalUrl';
import getAttachmentFileType from 'owa-attachment-model-store/lib/utils/getAttachmentFileType';
import { saveAttachmentsToCloud, SaveToCloudStatus } from 'owa-attachment-savetocloud';
import getImmutableEntryId from 'owa-immutable-id/lib/getImmutableEntryId';
import { selectMailStoreItemById } from 'owa-mail-store';
import AttachmentResultCode from 'owa-service/lib/contract/AttachmentResultCode';
import Message from 'owa-service/lib/contract/Message';
import SaveToCloudNotificationPayload from 'owa-service/lib/contract/SaveToCloudNotificationPayload';
import { setSaveToCloudStatus } from '../actions/internalActions';
import getOrCreateSxSStoreData from '../store/Store';
import tryGetAttachmentFullViewStateByIdInReadingPane from './tryGetAttachmentFullViewStateByIdInReadingPane';

const saveToCloudInternal = async (
    actionMessage: { entryPoint: string; sxsId: string },
    resolve?: () => void,
    reject?: (error?: any) => void
) => {
    const { entryPoint, sxsId } = actionMessage;
    const datapoint = new PerformanceDatapoint('AttachmentSaveToCloud');

    // We are creating a local copy of this here because setSaveToCloudStatus and setAttachmentOriginalUrl depend on the current attachmentId, and
    // if you change which attachment you are viewing while save is in progress attachmentId will have changed
    // by the time we call the function.
    const sxsStore = getOrCreateSxSStoreData(sxsId);
    const attachmentId = sxsStore.extendedViewState.attachmentId;
    const getAttachment = await lazyGetAttachment.import();
    const attachmentModel = getAttachment(attachmentId);
    const attachmentSize = attachmentModel ? attachmentModel.model.Size : 0;
    const datapointCustomData = {
        entryPoint: entryPoint,
        result: null,
        actionSource: AttachmentSelectionSource[sxsStore.extendedViewState.selectionSource],
    };
    const attachmentFullViewState = tryGetAttachmentFullViewStateByIdInReadingPane(
        attachmentId,
        sxsId
    );

    const fileType = getAttachmentFileType(attachmentModel);
    datapoint.addCosmosOnlyData(
        JSON.stringify({
            size: attachmentSize,
            fileType,
        })
    );

    if (attachmentFullViewState) {
        startFakeProgressIndicator(
            getSaveToCloudEstimatedCompleteTimeInMS(attachmentFullViewState.attachmentId),
            0.1,
            (completePercent: number) => {
                setActionCompletePercent(attachmentFullViewState, completePercent);
            },
            attachmentFullViewState.attachmentId
        );

        saveToCloudPreProcess(attachmentFullViewState);
    }

    const notificationHandler = (payload: SaveToCloudNotificationPayload): boolean => {
        datapointCustomData.result = payload.ResultCode;
        datapoint.addCustomData(datapointCustomData);

        const succeeded = payload.ResultCode === AttachmentResultCode.Success;

        if (attachmentFullViewState) {
            stopFakeProgressIndicator(attachmentFullViewState.attachmentId, succeeded);
            saveToCloudPostProcess(
                attachmentFullViewState,
                succeeded,
                succeeded ? payload.Item.Location : null,
                sxsId
            );
        }

        if (payload.ResultCode === AttachmentResultCode.Success) {
            setSaveToCloudStatus(SaveToCloudStatus.succeeded, attachmentId, sxsId);
            setAttachmentOriginalUrl(attachmentId, payload.Item.Location);
            datapoint.end();
            resolve?.();
        } else {
            setSaveToCloudStatus(SaveToCloudStatus.failed, attachmentId, sxsId);
            const errorObj = new Error(
                `ResultCode: ${payload.ResultCode} with exception: ${payload.Exception}`
            );
            datapoint.endWithError(DatapointStatus.ServerError, errorObj);
            reject?.(errorObj);
        }

        (async () => {
            const itemId = sxsStore.editState?.itemId?.Id;
            const message = (itemId && selectMailStoreItemById(itemId)) as Message;
            lazyLogSigsDatapoint.importAndExecute('SavedAttachment', {
                itemId: itemId && (await getImmutableEntryId(itemId)),
                customProperties: {
                    FileId: attachmentId.Id,
                    FileType: fileType,
                    Destination: 'Cloud',
                    Sender: (message?.From || message?.Sender)?.Mailbox?.EmailAddress,
                },
            });
        })();

        return true;
    };

    const serviceCallFailureCallback = (error: string): void => {
        setSaveToCloudStatus(SaveToCloudStatus.failed, attachmentId, sxsId);
        datapointCustomData.result = AttachmentResultCode.GenericFailure;
        datapoint.addCustomData(datapointCustomData);
        const errorObj = new Error(error);
        datapoint.endWithError(DatapointStatus.ServerError, errorObj);
        reject?.(errorObj);
    };

    setSaveToCloudStatus(SaveToCloudStatus.saving, attachmentId, sxsId);
    saveAttachmentsToCloud(
        attachmentId,
        null,
        sxsId,
        notificationHandler,
        serviceCallFailureCallback
    );
};

const saveToCloudFromSxS = async (entryPoint: string, sxsId: string) =>
    new Promise<void>((resolve, reject) =>
        saveToCloudInternal({ entryPoint, sxsId }, resolve, reject)
    );

export { saveToCloudInternal, saveToCloudFromSxS };
